package or.garden.manager.features.beds.components

import androidx.compose.runtime.*
import or.garden.manager.domain.model.PlantingSuggestionsResponse
import org.jetbrains.compose.web.dom.*

@Composable
fun AiSuggestionsView(suggestions: PlantingSuggestionsResponse) {
    Div({
        classes("space-y-6") // Removed mt-8 as parent already has gap
    }) {
        // Strategy Reasoning
        if (suggestions.layoutGuidelines.reasoning.isNotEmpty()) {
            Div({
                classes("bg-white", "rounded-2xl", "p-8", "border", "border-emerald-100", "shadow-sm")
            }) {
                H3({ classes("text-xl", "font-bold", "text-[#134130]", "mb-4", "flex", "items-center", "gap-2") }) {
                    I({ classes("fas", "fa-brain", "text-emerald-500") })
                    Text("Strategija rasporeda")
                }
                P({ classes("text-gray-600", "leading-relaxed") }) {
                    Text(suggestions.layoutGuidelines.reasoning)
                }
            }
        }

        // Sequential Zones
        if (suggestions.layoutGuidelines.sequentialZones.isNotEmpty()) {
            Div({
                classes("bg-white", "rounded-2xl", "p-8", "border", "border-gray-100", "shadow-sm")
            }) {
                H3({ classes("text-xl", "font-bold", "text-[#134130]", "mb-6") }) { Text("Prijedlog zona (redoslijed)") }
                Div({ classes("grid", "grid-cols-1", "md:grid-cols-2", "gap-4") }) {
                    suggestions.layoutGuidelines.sequentialZones.filter { it.zoneType != "pathway" }.distinctBy { it.cropAllocation?.cropName }.forEachIndexed { index, zone ->
                        Div({
                            classes("flex", "items-start", "gap-4", "p-4", "rounded-xl", "bg-emerald-50")
                        }) {
                            Div({
                                classes("w-8", "h-8", "rounded-full", "bg-white", "flex", "items-center", "justify-center", "font-bold", "text-emerald-700", "shadow-sm")
                            }) { Text((index + 1).toString()) }
                            
                            Div {
                                val title = "${zone.cropAllocation?.cropName} (${zone.lengthCm}cm)"
                                Span({ classes("font-bold", "text-gray-800", "block") }) { Text(title) }
                                
                                zone.cropAllocation?.companionPlants?.let { companions ->
                                    if (companions.isNotEmpty()) {
                                        Div({ classes("mt-2", "space-y-1") }) {
                                            Span({ classes("text-xs", "font-bold", "text-emerald-600", "uppercase") }) { Text("Pratioci:") }
                                            companions.forEach { companion ->
                                                Div({ classes("flex", "items-start", "gap-2", "text-sm", "text-gray-600") }) {
                                                    I({ classes("fas", "fa-check", "text-[10px]", "mt-1", "text-emerald-400") })
                                                    Div {
                                                        Span({ classes("font-semibold") }) { Text("${companion.name}: ") }
                                                        Div({ classes("text-xs", "font-bold", "text-purple-600", "mb-0.5") }) {
                                                            I({ classes("fas", "fa-map-marker-alt", "mr-1") })
                                                            Text(companion.placementAdvice)
                                                        }
                                                        Span { Text(companion.reasoning) }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        // Irrigation Advice
        if (suggestions.layoutGuidelines.irrigationAdvice.isNotEmpty()) {
            Div({
                classes("bg-white", "rounded-2xl", "p-8", "border", "border-blue-100", "shadow-sm")
            }) {
                H3({ classes("text-xl", "font-bold", "text-[#134130]", "mb-4", "flex", "items-center", "gap-2") }) {
                    I({ classes("fas", "fa-tint", "text-blue-500") })
                    Text("Savjeti za navodnjavanje")
                }
                P({ classes("text-gray-600", "leading-relaxed") }) {
                    Text(suggestions.layoutGuidelines.irrigationAdvice)
                }
            }
        }

        // Rotation Plan
        if (suggestions.rotationPlan.nextSeason.isNotEmpty()) {
            Div({
                classes("bg-white", "rounded-2xl", "p-8", "border", "border-orange-100", "shadow-sm")
            }) {
                H3({ classes("text-xl", "font-bold", "text-[#134130]", "mb-6", "flex", "items-center", "gap-2") }) {
                    I({ classes("fas", "fa-redo", "text-orange-500") })
                    Text("Plan plodoreda (sljedeća sezona)")
                }
                Div({ classes("grid", "grid-cols-1", "md:grid-cols-2", "gap-4") }) {
                    suggestions.rotationPlan.nextSeason.forEach { rotation ->
                        Div({
                            classes("p-4", "border", "border-orange-50", "rounded-xl", "bg-orange-50/30")
                        }) {
                            Span({ classes("text-xs", "font-bold", "text-orange-600", "uppercase", "block", "mb-1") }) { Text("Nakon: ${rotation.afterCrop}") }
                            Span({ classes("font-bold", "text-gray-800", "block", "mb-2") }) { Text("Sadi: ${rotation.plantNext.joinToString(", ")}") }
                            P({ classes("text-xs", "text-gray-500") }) { Text(rotation.reasoning) }
                        }
                    }
                }
            }
        }

        // Fertilization Plan
        suggestions.fertilizationPlan?.let { plan ->
            Div({
                classes("bg-white", "rounded-2xl", "p-8", "border", "border-purple-100", "shadow-sm")
            }) {
                H3({ classes("text-xl", "font-bold", "text-[#134130]", "mb-6", "flex", "items-center", "gap-2") }) {
                    I({ classes("fas", "fa-flask", "text-purple-500") })
                    Text("Plan đubrenja (ishrana biljaka)")
                }
                Div({ classes("grid", "grid-cols-1", "md:grid-cols-3", "gap-4") }) {
                    // Phase items
                    listOf(
                        Triple("Presađivanje", plan.transplantingPhase, "fa-seedling"),
                        Triple("Cvatnja", plan.floweringPhase, "fa-sun"),
                        Triple("Plodonošenje", plan.fruitingPhase, "fa-apple-alt")
                    ).forEach { (title, phase, icon) ->
                        Div({
                            classes("p-5", "border", "border-purple-50", "rounded-xl", "bg-purple-50/30", "flex", "flex-col", "gap-3")
                        }) {
                            Div({ classes("flex", "items-center", "gap-2") }) {
                                I({ classes("fas", icon, "text-purple-400", "text-sm") })
                                Span({ classes("text-xs", "font-bold", "text-purple-600", "uppercase") }) { Text(title) }
                            }
                            
                            Div({ classes("bg-white/60", "px-3", "py-1.5", "rounded-lg", "self-start", "border", "border-purple-100") }) {
                                Span({ classes("font-black", "text-purple-700", "text-sm") }) { Text("NPK: ${phase.ratio}") }
                            }
                            
                            P({ classes("text-sm", "text-gray-600", "leading-relaxed") }) { Text(phase.advice) }
                        }
                    }
                }
            }
        }

        // Practical Advice
        if (suggestions.practicalAdvice.isNotEmpty()) {
            Div({
                classes("bg-white", "rounded-2xl", "p-8", "border", "border-gray-100", "shadow-sm")
            }) {
                H3({ classes("text-xl", "font-bold", "text-[#134130]", "mb-6") }) { Text("Praktični savjeti") }
                Div({ classes("grid", "grid-cols-1", "md:grid-cols-2", "gap-4") }) {
                    suggestions.practicalAdvice.forEach { advice ->
                        Div({ classes("flex", "items-start", "gap-3", "p-4", "bg-gray-50", "rounded-xl") }) {
                            I({ classes("fas", "fa-lightbulb", "text-amber-400", "mt-1") })
                            Text(advice)
                        }
                    }
                }
            }
        }
    }
}
